// What the brand's campaigns are about (Brúnó, 2026-10-15): an app, a physical
// product, a service. Creators see the product's name wherever the brand
// shows, never the company's legal name, which stays in the agreement and on
// the invoices. A new brand adds its first product in the setup, after the
// company details; Settings → Products keeps the list, and each campaign is
// for one of them (data/brand-products.ts).

import {useEffect, useRef, useState} from 'react';
import type {FormEvent, ReactNode} from 'react';
import {useNavigate} from 'react-router-dom';
import type {BrandProduct, BrandProductRequest, ProductKind} from '../../shared/brand-api';
import {Icon} from '../creator/components/Icon';
import {BrandApiError, describe, lookupProduct, removeProduct, saveProduct, uploadProductIcon} from './api';
import {InfoTip} from './info-tip';
import {useLeaveWarning, usePageTitle} from './page-title';
import {SettingsHead} from './settings';
import {SetupNav, setupStep} from './setup';
import {useBrand} from './store';
import './products.css';

export const PRODUCT_KINDS: readonly [ProductKind, string, string][] = [
  ['app', 'App', 'On the App Store or Google Play.'],
  ['physical', 'Physical product', 'Something people buy and hold.'],
  ['service', 'Service or website', 'Something people use or book.'],
  ['other', 'Something else', 'An event, a place, a cause.'],
];

const KIND_LABEL = Object.fromEntries(PRODUCT_KINDS.map(([kind, label]) => [kind, label])) as Record<ProductKind, string>;
const STORE_LINK = /^https:\/\/(apps\.apple\.com|play\.google\.com)\//i;
const ICON_TYPES = ['image/png', 'image/jpeg', 'image/webp'];
const MAX_ICON_BYTES = 2 * 1024 * 1024;

function Field({label, error, children}: {label: ReactNode; error?: string; children: ReactNode}) {
  return <label className={`brand-field${error ? ' field-error' : ''}`}><span>{label}</span>{children}{error ? <em>{error}</em> : null}</label>;
}

/** The product's icon, or its first letter on a tile while it has none. */
export function ProductMark({product, src, size = 40}: {product: Pick<BrandProduct, 'name'>; src?: string; size?: number}) {
  return src
    ? <img className="product-mark" src={src} alt="" width={size} height={size} style={{width: size, height: size}} />
    : <span className="product-mark product-mark-blank" style={{width: size, height: size, fontSize: Math.round(size * .42)}} aria-hidden="true">
      {product.name.trim().charAt(0).toUpperCase() || <Icon name="image" size={Math.round(size * .45)} />}</span>;
}

/**
 * The product's details. An App Store or Google Play link fills in the name
 * and the icon from the store. Saved, it answers the brand's list as it is
 * now. In the setup, the setup's own Continue submits it (`setupNext`).
 */
export function ProductForm({product, formId, setupNext, inline = false, onSaved, onCancel}: {
  product: BrandProduct | null;
  formId: string;
  /** Inside a card or a dialog already: no card of its own. */
  inline?: boolean;
  /** In the setup: what the floating Continue leads to; it submits this form. */
  setupNext?: string;
  onSaved(saved: BrandProduct, products: BrandProduct[]): void | Promise<void>;
  onCancel?(): void;
}) {
  const {toast} = useBrand();
  const [kind, setKind] = useState<ProductKind | ''>(product?.kind ?? '');
  const [name, setName] = useState(product?.name ?? '');
  const [description, setDescription] = useState(product?.description ?? '');
  const [website, setWebsite] = useState(product?.website_url ?? '');
  const [appStore, setAppStore] = useState(product?.app_store_url ?? '');
  const [play, setPlay] = useState(product?.play_store_url ?? '');
  // The store's icon a lookup found: copied by the server when the product is saved.
  const [storeIcon, setStoreIcon] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState('');
  const [looking, setLooking] = useState('');
  const [looked, setLooked] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const picker = useRef<HTMLInputElement>(null);
  useLeaveWarning(dirty);
  useEffect(() => {
    if (!file) { setPreview(''); return; }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function forget(key: string) {
    setDirty(true);
    if (errors[key]) setErrors((current) => Object.fromEntries(Object.entries(current).filter(([field]) => field !== key)));
  }

  /** A store link pasted in fills in what the store knows, once per link. */
  async function readStore(link: string, field: 'app_store_url' | 'play_store_url') {
    const url = link.trim();
    if (!STORE_LINK.test(url) || url === looked) return;
    setLooking(field); setLooked(url);
    try {
      const {lookup} = await lookupProduct(url);
      if (lookup.app_store_url) setAppStore(lookup.app_store_url);
      if (lookup.play_store_url) setPlay(lookup.play_store_url);
      if (!name.trim()) setName(lookup.name);
      if (lookup.icon_source && !file && !product?.icon_url) setStoreIcon(lookup.icon_source);
    } catch (caught) {
      if (caught instanceof BrandApiError && caught.fields.url) setErrors((current) => ({...current, [field]: caught.fields.url!}));
    } finally { setLooking(''); }
  }

  function chooseIcon(chosen: File | undefined) {
    if (!chosen) return;
    if (!ICON_TYPES.includes(chosen.type)) { setErrors((current) => ({...current, icon: 'Use a PNG, JPEG or WebP image.'})); return; }
    if (chosen.size > MAX_ICON_BYTES) { setErrors((current) => ({...current, icon: 'Keep the icon under 2 MB.'})); return; }
    forget('icon'); setFile(chosen); setStoreIcon('');
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    const missing: Record<string, string> = {};
    if (!kind) missing.kind = 'Choose what kind of product it is.';
    if (name.trim().length < 2) missing.name = 'Name the product the way creators will know it.';
    if (Object.keys(missing).length) { setErrors(missing); toast('Fill in the fields marked in red.', true); return; }
    const app = kind === 'app';
    const input: BrandProductRequest = {
      name: name.trim(), kind: kind as ProductKind, description: description.trim(),
      website_url: app ? '' : website.trim(), app_store_url: app ? appStore.trim() : '', play_store_url: app ? play.trim() : '',
      ...(storeIcon && !file ? {icon_source: storeIcon} : {}),
    };
    setBusy(true); setErrors({});
    try {
      let saved = await saveProduct(product?.id ?? null, input);
      if (file) {
        try { saved = await uploadProductIcon(saved.product.id, file); }
        catch (caught) { toast(`The product was saved, but not its icon. ${describe(caught)}`, true); }
      }
      setDirty(false);
      await onSaved(saved.product, saved.products);
    } catch (caught) {
      if (caught instanceof BrandApiError) setErrors(caught.fields);
      toast(describe(caught), true);
    } finally { setBusy(false); }
  }

  const icon = preview || storeIcon || product?.icon_url || '';
  return <form id={formId} className={`brand-form product-form${inline ? ' inline' : ' card'}`} noValidate onSubmit={(event) => void submit(event)}>
    <div className="take-choice"><span className="field-title">What is it?</span>
      <div className="cadence-choice product-kinds" role="radiogroup" aria-label="Kind of product">
        {PRODUCT_KINDS.map(([value, title, copy]) => <label className={kind === value ? 'active' : ''} key={value}>
          <input type="radio" name="kind" value={value} checked={kind === value} onChange={() => { setKind(value); forget('kind'); }} />
          <span><b>{title}</b><small>{copy}</small></span></label>)}
      </div>{errors.kind ? <p className="form-alert" role="alert">{errors.kind}</p> : null}</div>
    {kind === 'app' ? <div className="form-grid">
      <Field label={<>App Store link<InfoTip text="Paste it and we fill in the name and the icon from the store." /></>} error={errors.app_store_url}>
        <input name="app_store_url" value={appStore} inputMode="url" placeholder="https://apps.apple.com/…" onChange={(event) => { setAppStore(event.target.value); forget('app_store_url'); }}
          onBlur={(event) => void readStore(event.target.value, 'app_store_url')} />
        {looking === 'app_store_url' ? <small className="product-looking">Reading the App Store…</small> : null}</Field>
      <Field label="Google Play link" error={errors.play_store_url}>
        <input name="play_store_url" value={play} inputMode="url" placeholder="https://play.google.com/store/apps/…" onChange={(event) => { setPlay(event.target.value); forget('play_store_url'); }}
          onBlur={(event) => void readStore(event.target.value, 'play_store_url')} />
        {looking === 'play_store_url' ? <small className="product-looking">Reading Google Play…</small> : null}</Field>
    </div> : null}
    <div className="product-name-row">
      <button className="product-icon-pick" type="button" onClick={() => picker.current?.click()} aria-label={icon ? 'Change the icon' : 'Upload an icon'}>
        <ProductMark product={{name}} src={icon} size={64} /><span className="product-icon-label"><Icon name="upload" size={13} />{icon ? 'Change' : 'Icon'}</span>
      </button>
      <input ref={picker} type="file" accept={ICON_TYPES.join(',')} hidden onChange={(event) => { chooseIcon(event.target.files?.[0]); event.target.value = ''; }} />
      <Field label={<>Name<InfoTip text="What creators see in place of your company: in their jobs, the brief, the chat and the campaign page." /></>} error={errors.name || errors.icon}>
        <input name="name" value={name} maxLength={80} placeholder={kind === 'app' ? 'Pixelfox' : 'Glow Serum'} onChange={(event) => { setName(event.target.value); forget('name'); }} />
      </Field>
    </div>
    <div className="form-grid">
      <Field label={<>In one line<InfoTip text="What it is, for a creator who has never heard of it. They read it before your brief." /></>} error={errors.description}>
        <input name="description" value={description} maxLength={160} placeholder={kind === 'app' ? 'A photo editor for your phone' : 'A vitamin C serum for daily use'}
          onChange={(event) => { setDescription(event.target.value); forget('description'); }} />
      </Field>
      {kind && kind !== 'app' ? <Field label="Website" error={errors.website_url}>
        <input name="website_url" value={website} inputMode="url" placeholder="yourproduct.com" onChange={(event) => { setWebsite(event.target.value); forget('website_url'); }} />
      </Field> : null}
    </div>
    {setupNext ? <SetupNav next={setupNext} form={formId} busy={busy} /> : <div className="form-actions">
      {onCancel ? <button className="btn btn-ghost" type="button" onClick={onCancel}>Cancel</button> : null}
      <button className="btn btn-g" disabled={busy}>{busy ? 'Saving…' : product ? 'Save product' : 'Add product'}</button>
    </div>}
  </form>;
}

/** Adding a product without leaving the page (the campaign editor). */
export function AddProductDialog({onCancel, onAdded}: {onCancel(): void; onAdded(product: BrandProduct): void}) {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);
  return <div className="switch-layer">
    <div className="switch-backdrop" onClick={onCancel} />
    <div className="card switch-dialog product-dialog" role="dialog" aria-modal="true" aria-labelledby="add-product-title">
      <button className="switch-close" type="button" onClick={onCancel} aria-label="Close"><Icon name="x" size={18} /></button>
      <h2 id="add-product-title">Add a product</h2>
      <ProductForm product={null} formId="add-product-form" inline onCancel={onCancel} onSaved={(saved) => onAdded(saved)} />
    </div>
  </div>;
}

/** The campaign editor's choice of product: one tile each, and a way to add one. */
export function ProductPicker({products, value, error, onChange, onAdd}: {
  products: BrandProduct[]; value: string | undefined; error?: string; onChange(id: string): void; onAdd(): void;
}) {
  return <div className="take-choice"><span className="field-title">Product<InfoTip text="Creators see its name and icon, never your company’s name." /></span>
    <div className="cadence-choice product-choice" role="radiogroup" aria-label="Product">
      {products.map((product) => <label className={value === product.id ? 'active' : ''} key={product.id}>
        <input type="radio" name="product_id" value={product.id} checked={value === product.id} onChange={() => onChange(product.id)} />
        <ProductMark product={product} src={product.icon_url} size={36} />
        <span><b>{product.name}</b><small>{KIND_LABEL[product.kind]}</small></span>
      </label>)}
      <button className="product-add" type="button" onClick={onAdd}><Icon name="plus" size={17} />Add product</button>
    </div>{error ? <p className="form-alert" role="alert">{error}</p> : null}</div>;
}

/**
 * /products: in the setup, the first product on its own step; afterwards a
 * tab of Settings with the list, where a product is added, edited or removed.
 */
export function ProductsPage() {
  const {data, reload, toast} = useBrand();
  const navigate = useNavigate();
  const [editing, setEditing] = useState<string | null>(null);
  const [removing, setRemoving] = useState('');
  usePageTitle('Products');
  if (!data) return null;
  const signed = data.agreement.status === 'signed';
  if (setupStep(data) !== null) {
    return <div className="page setup-page">
      <header className="page-head"><div><h1>What will creators film?</h1><p className="lead">Creators see this name, never your company’s legal name.</p></div></header>
      <ProductForm product={data.products[0] ?? null} formId="product-form" setupNext={signed ? 'Next: choose your plan' : 'Next: read and accept the agreement'}
        onSaved={async () => { await reload(false); navigate(signed ? '/plan' : '/agreement'); }} />
    </div>;
  }
  async function remove(product: BrandProduct) {
    if (!window.confirm(`Remove ${product.name}? Its campaigns keep it; new campaigns can’t choose it.`)) return;
    setRemoving(product.id);
    try { await removeProduct(product.id); await reload(false); toast(`${product.name} removed`); }
    catch (caught) { toast(describe(caught), true); }
    finally { setRemoving(''); }
  }
  const done = async (saved: BrandProduct) => { setEditing(null); await reload(false); toast(`${saved.name} saved`); };
  return <div className="page"><SettingsHead />
    <section className="card product-list" aria-label="Products">
      {data.products.map((product) => editing === product.id
        ? <ProductForm key={product.id} product={product} formId={`product-${product.id}`} inline onCancel={() => setEditing(null)} onSaved={done} />
        : <div className="product-row" key={product.id}>
          <ProductMark product={product} src={product.icon_url} size={48} />
          <div className="product-row-text"><b>{product.name}</b>
            <small>{KIND_LABEL[product.kind]} · {product.campaigns} {product.campaigns === 1 ? 'campaign' : 'campaigns'}</small>
            {product.description ? <p>{product.description}</p> : null}</div>
          <div className="product-row-actions">
            <button className="btn btn-light" type="button" onClick={() => setEditing(product.id)}><Icon name="edit" size={16} />Edit</button>
            {data.products.length > 1 ? <button className="btn btn-ghost" type="button" disabled={removing === product.id} onClick={() => void remove(product)}
              aria-label={`Remove ${product.name}`}><Icon name="trash" size={16} /></button> : null}
          </div>
        </div>)}
      {editing === 'new'
        ? <ProductForm product={null} formId="product-new" inline onCancel={() => setEditing(null)} onSaved={done} />
        : <button className="product-add wide" type="button" onClick={() => setEditing('new')}><Icon name="plus" size={17} />Add product</button>}
    </section>
  </div>;
}
